import React, { useState } from "react";
import { useFetch } from "@/hooks/useApi";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, Loader, StatusPill, Plate, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { num, ukDate } from "@/lib/format";
import * as Icons from "lucide-react";
import { toast } from "sonner";

const empty = {
  registration: "", make_model: "", year: "", current_mileage: "", assigned_driver_id: "",
  insurance_expiry: "", mot_expiry: "", road_tax_expiry: "", service_due: "",
  fuel_efficiency: "8", fuel_variance_pct: "10", status: "active",
};

export default function Vans() {
  const { data: vans, loading, refetch } = useFetch("/vans");
  const { data: drivers } = useFetch("/drivers");
  const [open, setOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const driverName = (id) => drivers?.find((d) => d.id === id)?.name || "—";

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (v) => {
    setForm({ ...v, current_mileage: String(v.current_mileage), fuel_efficiency: String(v.fuel_efficiency), fuel_variance_pct: String(v.fuel_variance_pct) });
    setEditId(v.id); setOpen(true);
  };
  const viewHistory = async (reg) => {
    const { data } = await api.get(`/vans/${reg}/assignments`);
    setAssignments(data); setAssignOpen(reg);
  };

  const save = async () => {
    try {
      if (editId) await api.put(`/vans/${editId}`, form);
      else await api.post("/vans", form);
      toast.success(editId ? "Van updated" : "Van created");
      setOpen(false); refetch();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this van?")) return;
    try { await api.delete(`/vans/${id}`); toast.success("Deleted"); refetch(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (loading) return <Loader />;
  const soon = new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10);

  return (
    <div className="fp-fade-in">
      <PageHeader title="Vans" subtitle={`${vans?.length || 0} vehicles`} testId="vans-page">
        <Button onClick={openNew} className="w-full sm:w-auto" data-testid="add-van-button"><Icons.Plus className="h-4 w-4 mr-1.5" /> Add Van</Button>
      </PageHeader>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Registration</TableHead>
                <TableHead>Make / Model</TableHead>
                <TableHead>Mileage</TableHead>
                <TableHead>Target mi/L</TableHead>
                <TableHead>Driver</TableHead>
                <TableHead>MOT</TableHead>
                <TableHead>Insurance</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(vans || []).map((v) => (
                <TableRow key={v.id} data-testid={`van-row-${v.registration}`}>
                  <TableCell><Plate reg={v.registration} /></TableCell>
                  <TableCell><p className="font-medium">{v.make_model}</p><p className="text-xs text-muted-foreground">{v.year}</p></TableCell>
                  <TableCell className="font-mono">{num(v.current_mileage)} mi</TableCell>
                  <TableCell className="font-mono">{v.fuel_efficiency}</TableCell>
                  <TableCell>{driverName(v.assigned_driver_id)}</TableCell>
                  <TableCell className={`font-mono text-sm ${v.mot_expiry && v.mot_expiry <= soon ? "text-amber-400" : ""}`}>{ukDate(v.mot_expiry)}</TableCell>
                  <TableCell className={`font-mono text-sm ${v.insurance_expiry && v.insurance_expiry <= soon ? "text-amber-400" : ""}`}>{ukDate(v.insurance_expiry)}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button variant="ghost" size="icon" onClick={() => viewHistory(v.registration)} title="Assignment history" data-testid={`history-van-${v.registration}`}><Icons.History className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => openEdit(v)}><Icons.Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(v.id)}><Icons.Trash2 className="h-4 w-4 text-rose-400" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {(!vans || vans.length === 0) && <EmptyState icon="Truck" title="No vans yet" subtitle="Add your first vehicle." />}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="van-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit Van" : "New Van"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Registration</Label><Input value={form.registration} onChange={set("registration")} placeholder="EF70 KLP" className="mt-1.5 font-mono" data-testid="van-reg-input" /></div>
            <div><Label>Make / Model</Label><Input value={form.make_model} onChange={set("make_model")} className="mt-1.5" data-testid="van-model-input" /></div>
            <div><Label>Year</Label><Input value={form.year} onChange={set("year")} className="mt-1.5" /></div>
            <div><Label>Current Mileage</Label><Input type="number" value={form.current_mileage} onChange={set("current_mileage")} className="mt-1.5" /></div>
            <div>
              <Label>Assigned Driver</Label>
              <Select value={form.assigned_driver_id || "none"} onValueChange={(v) => set("assigned_driver_id")(v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1.5" data-testid="van-driver-select"><SelectValue placeholder="Select driver" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {(drivers || []).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Fuel Efficiency (mi/L)</Label><Input type="number" step="0.1" value={form.fuel_efficiency} onChange={set("fuel_efficiency")} className="mt-1.5" data-testid="van-efficiency-input" /></div>
            <div><Label>Insurance Expiry</Label><Input type="date" value={form.insurance_expiry} onChange={set("insurance_expiry")} className="mt-1.5" /></div>
            <div><Label>MOT Expiry</Label><Input type="date" value={form.mot_expiry} onChange={set("mot_expiry")} className="mt-1.5" /></div>
            <div><Label>Road Tax Expiry</Label><Input type="date" value={form.road_tax_expiry} onChange={set("road_tax_expiry")} className="mt-1.5" /></div>
            <div><Label>Service Due</Label><Input type="date" value={form.service_due} onChange={set("service_due")} className="mt-1.5" /></div>
            <div><Label>Acceptable Fuel Variance %</Label><Input type="number" value={form.fuel_variance_pct} onChange={set("fuel_variance_pct")} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-van-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!assignOpen} onOpenChange={() => setAssignOpen(null)}>
        <DialogContent data-testid="assignment-history-dialog">
          <DialogHeader><DialogTitle>Assignment History · {assignOpen}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {assignments.length === 0 && <p className="text-sm text-muted-foreground">No assignment history.</p>}
            {assignments.map((a) => (
              <div key={a.id} className="flex items-center justify-between border border-border rounded-lg p-3">
                <div>
                  <p className="font-medium">{a.driver_name}</p>
                  <p className="text-xs text-muted-foreground">{ukDate(a.start_date)} → {a.end_date ? ukDate(a.end_date) : "Present"}</p>
                </div>
                {!a.end_date && <StatusPill status="active" label="current" />}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
